import styled from '@emotion/styled';
import {
  ApexChartWrapper,
  useGlimmerTheme,
} from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';
import { getAlignedData } from './utils/get-aligned-data';
import { getColor } from './utils/get-color';
import { getMultilineLabel } from './utils/get-multiline-label';
import type { BlockAverageData } from './utils/types';

type Props = {
  studentAverages: BlockAverageData[];
  classroomAverages: BlockAverageData[];
};

export function DigitalPracticeBarChart({
  studentAverages,
  classroomAverages,
}: Props): JSX.Element {
  const theme = useGlimmerTheme();
  const { t } = useTranslation();

  const alignedData = getAlignedData({ classroomAverages, studentAverages });

  const chartSeries = [
    {
      name: t('common.student'),
      data: alignedData.map((block) => block.studentAverage),
    },
    {
      name: t('common.classroom'),
      data: alignedData.map((block) => block.classroomAverage),
      color: theme.tokens.color.specific.reports.semantic['no-data'].value,
    },
  ];

  const chartOptions = {
    chart: {
      type: 'bar' as const,
      fontFamily: 'Roboto, Arial, sans-serif',
      toolbar: {
        show: false,
      },
      selection: {
        yaxis: {
          min: 0,
          max: 10,
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '16px',
        borderRadius: 2,
        borderRadiusApplication: 'end' as const,
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent', 'transparent'],
    },
    xaxis: {
      categories: alignedData.map((block) =>
        getMultilineLabel(t(block.blockTitle?.text || ''))
      ),
      axisTicks: {
        show: false,
        height: 100,
      },
      tickPlacement: 'on',
      axisBorder: {
        show: true,
        color: 'black',
        height: 1,
      },
      labels: {
        rotate: 0,
        hideOverlappingLabels: false,
        trim: true,
      },
    },
    yaxis: {
      decimalsInFloat: 1,
      tickAmount: 5,
      max: 10,
    },
    fill: {
      opacity: 1,
    },
    colors: [getColor(theme)],
  };

  return (
    <ChartWrapper id="chart">
      <ApexChartWrapper
        id="digital-practice-bar-chart"
        options={chartOptions}
        series={chartSeries}
        type="bar"
        height={'190px'}
        width={'100%'}
      />
    </ChartWrapper>
  );
}

const ChartWrapper = styled.div`
  max-width: 534px;
  width: 100%;
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 100%;
  }
`;
